/**
 * SkyUfo.js
 *
 * The landing sky's Easter egg: a small saucer that crosses the sky about once
 * every ten minutes, hovers for two seconds in the lower right, and leaves.
 *
 * Design of record: docs/design/landing-sky/landing-sky-ufo-reference.html
 * (approved October 19, 2026). The artwork, the track, the timing and the
 * easing below are taken from that file; change them there first.
 *
 * SCOPE (ruled October 19, 2026)
 * ------------------------------
 *   - Landing page only. SkyPage renders this only when its `ufo` prop is true,
 *     and only LandingSky passes it. /login, /join-waitlist and
 *     /forgot-password never mount it.
 *   - Switched by SHOW_UFO below — a named constant, not an environment
 *     variable, following SHOW_STAR_LINES in starField.js. With it false,
 *     SkyPage does not mount this component at all: nothing is rendered and no
 *     timer is created.
 *   - prefers-reduced-motion: reduce hides it outright, like the streaks, and
 *     no timer is created. Read once at mount; the reduced-motion block at the
 *     end of SkyPage.css also hides the element if the setting changes later.
 *   - Where CSS motion paths are unsupported (offset-path needs Safari 16,
 *     Chrome 55, Firefox 72) it renders nothing. There is deliberately no
 *     transform-animation fallback.
 *
 * TIMING
 * ------
 * The first pass is due INTERVAL_MS (+/- JITTER) after mount; each later pass
 * the same interval after the previous one ENDS. A pass whose timer fires while
 * the tab is hidden is rescheduled, not spent. On unmount the timer is cleared
 * and any running animation is cancelled, so nothing outlives the component.
 *
 * The flight is a CSS motion path: offset-path is set from a track built from
 * the viewport at flight time, and offset-distance is animated through the Web
 * Animations API. Neither is React state — both are written through the ref, so
 * a re-render of the sky cannot reset a flight in progress.
 */

import React, { useEffect, useRef, useState } from 'react';

// The switch. true shows the saucer on the landing page; false removes it
// entirely (SkyPage does not mount this component).
export const SHOW_UFO = true;

// Approved settings (landing-sky-ufo-reference.html).
const CROSSING_S = 20;            // the whole pass, hover included
const HOVER_MS = 2000;            // the pause at the low point
const INTERVAL_MS = 10 * 60 * 1000;
const JITTER = 0.2;               // +/- fraction of the interval

// The mountain band's height in the desktop layout (--landing-sky-range-h).
// The low point is clamped to stay CLEARANCE_PX above it on short windows.
const RANGE_H = 230;
const CLEARANCE_PX = 50;

// Unique within the page: only the landing page mounts a saucer.
const DOME_CLIP_ID = 'landing-sky-ufo-dome';

const SVG_NS = 'http://www.w3.org/2000/svg';

const canFly = () =>
  typeof window !== 'undefined' &&
  typeof window.CSS !== 'undefined' &&
  window.CSS.supports('offset-path', 'path("M 0 0 L 1 1")') &&
  typeof Element !== 'undefined' &&
  typeof Element.prototype.animate === 'function' &&
  !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * One smooth track, built from the viewport at flight time: level in from the
 * left at 18% of the height, straight to 24% of the width, a cubic descent that
 * passes to the right of the hero, a low point at 80% of the width minus 72px
 * (clamped to stay above the mountain band), then a rise off the right edge.
 * Identical to buildTrack() in the reference.
 *
 * @returns {{ d: string, hoverAt: number }} the path, and the low point's
 *   position along it as a fraction of its total length
 */
const buildTrack = (W, H) => {
  const floor = H - RANGE_H - CLEARANCE_PX;
  const yTop = Math.round(0.18 * H);
  const yLow = Math.min(Math.round(0.77 * H), floor);
  const yExit = Math.min(Math.round(0.64 * H), floor - 40);
  const p = (x, y) => `${Math.round(x)} ${Math.round(y)}`;

  const d =
    `M ${p(-80, yTop)}` +
    ` L ${p(0.24 * W, yTop)}` +
    ` C ${p(0.40 * W, yTop)}, ${p(0.46 * W, 0.46 * H)}, ${p(0.62 * W, Math.min(0.62 * H, yLow - 40))}` +
    ` C ${p(0.71 * W - 72, yLow - 6)}, ${p(0.75 * W - 72, yLow)}, ${p(0.80 * W - 72, yLow)}` +
    ` C ${p(0.88 * W - 36, yLow)}, ${p(0.95 * W, yExit + 10)}, ${p(W + 80, yExit)}`;

  // Where along the path the low point sits: walk a detached probe path and
  // take the length whose x is nearest the low point's x.
  const probe = document.createElementNS(SVG_NS, 'path');
  probe.setAttribute('d', d);
  const total = probe.getTotalLength();
  const targetX = 0.80 * W - 72;
  let best = 0;
  let bestDist = Infinity;
  for (let l = 0; l <= total; l += 4) {
    const dist = Math.abs(probe.getPointAtLength(l).x - targetX);
    if (dist < bestDist) {
      bestDist = dist;
      best = l;
    }
  }

  return { d, hoverAt: best / total };
};

const nextDelay = () => INTERVAL_MS * (1 - JITTER + Math.random() * 2 * JITTER);

const SkyUfo = () => {
  // Decided once, at mount. Unsupported or reduced motion: render nothing and
  // schedule nothing.
  const [enabled] = useState(canFly);
  const ufoRef = useRef(null);

  useEffect(() => {
    if (!enabled) return undefined;

    let timer = null;
    let flight = null;

    const fly = () => {
      const node = ufoRef.current;
      if (!node) return;

      const track = buildTrack(window.innerWidth, window.innerHeight);
      const moveMs = Math.max(4000, CROSSING_S * 1000 - HOVER_MS);
      const tIn = moveMs * track.hoverAt;          // constant pace to the hover
      const tTotal = moveMs + HOVER_MS;
      const at = `${(track.hoverAt * 100).toFixed(2)}%`;

      node.style.offsetPath = `path("${track.d}")`;
      node.style.opacity = '1';

      // The hover is two keyframes at the same offset-distance.
      flight = node.animate(
        [
          { offsetDistance: '0%', offset: 0, easing: 'cubic-bezier(.42,0,.3,1)' },
          { offsetDistance: at, offset: tIn / tTotal, easing: 'linear' },
          { offsetDistance: at, offset: (tIn + HOVER_MS) / tTotal, easing: 'cubic-bezier(.6,0,.6,1)' },
          { offsetDistance: '100%', offset: 1 }
        ],
        { duration: tTotal, fill: 'forwards' }
      );

      flight.onfinish = () => {
        node.style.opacity = '0';
        flight = null;
        schedule();
      };
    };

    // Measured from mount, then from the end of each pass. A timer that fires
    // while the tab is hidden reschedules rather than flying unseen.
    function schedule() {
      clearTimeout(timer);
      timer = setTimeout(() => {
        if (document.hidden) {
          schedule();
          return;
        }
        fly();
      }, nextDelay());
    }

    schedule();

    return () => {
      clearTimeout(timer);
      if (flight) {
        flight.onfinish = null;
        flight.cancel();
        flight = null;
      }
    };
  }, [enabled]);

  if (!enabled) return null;

  // Artwork from the reference: a silhouette in the middle range's colour, a
  // hairline rim light, a dome in the pale star teal, portholes in the warm star
  // colour, the alien in a muted green and clipped to the dome. Rim light,
  // running lights and alien are all on (approved settings), so the reference's
  // show-* toggles are baked in.
  return (
    <svg
      className="landing-sky-ufo"
      ref={ufoRef}
      viewBox="0 0 72 40"
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <clipPath id={DOME_CLIP_ID}>
          <path d="M22 20 A14 13 0 0 1 50 20 Z" />
        </clipPath>
      </defs>
      <g clipPath={`url(#${DOME_CLIP_ID})`}>
        <path d="M30 19.5 C30 8.5, 42 8.5, 42 19.5 L42 24 L30 24 Z" fill="#9FC9A4" />
        <ellipse cx="33.5" cy="14.5" rx="2.1" ry="1.2" transform="rotate(-18 33.5 14.5)" fill="#0C1D25" />
        <ellipse cx="38.5" cy="14.5" rx="2.1" ry="1.2" transform="rotate(18 38.5 14.5)" fill="#0C1D25" />
      </g>
      <path
        d="M22 20 A14 13 0 0 1 50 20 Z"
        fill="rgba(207,237,238,0.12)"
        stroke="rgba(170,222,226,0.28)"
        strokeWidth="0.8"
      />
      <ellipse cx="36" cy="25" rx="34" ry="8" fill="#0C1D25" />
      <ellipse
        cx="36"
        cy="25"
        rx="33.5"
        ry="7.5"
        fill="none"
        stroke="rgba(170,222,226,0.42)"
        strokeWidth="0.9"
      />
      <path d="M8 27.5 Q36 36 64 27.5" fill="none" stroke="rgba(7,11,22,0.7)" strokeWidth="0.8" />
      <g className="landing-sky-ufo-lights">
        <circle className="landing-sky-ufo-lamp" cx="20" cy="26" r="1.6" fill="#FFD7AE" />
        <circle className="landing-sky-ufo-lamp" cx="36" cy="27" r="1.6" fill="#FFD7AE" />
        <circle className="landing-sky-ufo-lamp" cx="52" cy="26" r="1.6" fill="#FFD7AE" />
      </g>
    </svg>
  );
};

export default SkyUfo;
